import { useTRPC } from "@/lib/trpc";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ClockIcon } from "lucide-react";
import { toast } from "sonner";
import { getScheduleLabel } from "./utils";
import { format } from "date-fns";
import { SearchMetadataPill } from "../search-metadata-pill";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export const SearchScheduler = ({ searchSpecificationId }: { searchSpecificationId: string }) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const noBatchRequest = { trpc: { context: { noBatch: true } } } as const;

  const { data: searchSpecification } = useQuery(
    trpc.searchSpecifications.getSearchSpecification.queryOptions({
      id: searchSpecificationId,
    }, noBatchRequest),
  );
  const updateSearchSpecificationMutation = useMutation(
    trpc.searchSpecifications.updateSearchSpecification.mutationOptions({
      onSuccess: () => {
        toast.success("Schedule updated");
        void queryClient.invalidateQueries({
          queryKey: trpc.searchSpecifications.getSearchSpecification.queryKey({
            id: searchSpecificationId,
          }),
        });
      },
    }),
  );
  const cadence = searchSpecification?.cadence;
  const scheduleLabel = getScheduleLabel(cadence);

  const onSetSchedule = (nextCadence: "DAILY" | "WEEKLY" | "MONTHLY" | null) => {
    updateSearchSpecificationMutation.mutate({
      id: searchSpecificationId,
      cadence: nextCadence,
    });
  };

  const nextRun = searchSpecification?.nextScheduledAt ? new Date(searchSpecification.nextScheduledAt) : null;

  return (
    <div>
      <DropdownMenu>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button type="button" className="focus:outline-none text-left">
                  <SearchMetadataPill
                    icon={ClockIcon}
                    label="Schedule"
                    value={cadence ? scheduleLabel : "Not scheduled"}
                    tone={cadence ? "success" : "neutral"}
                    interactive
                    trailing={<ChevronDown className="h-3.5 w-3.5" />}
                  />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              {nextRun ? `Next run scheduled for ${format(new Date(nextRun), "MMM d, yyyy h:mm a")}` : "No schedule set"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent>
          <DropdownMenuItem className="cursor-pointer" onClick={() => onSetSchedule(null)}>
            Off
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => onSetSchedule("DAILY")}>
            Daily
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => onSetSchedule("WEEKLY")}>
            Weekly
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer" onClick={() => onSetSchedule("MONTHLY")}>
            Monthly
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
